import React from 'react'

import { observer } from 'mobx-react-lite'

import Avatar from '@mui/material/Avatar'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { useTheme } from '@mui/material/styles'

import { Text, Toast } from '@mxenabled/mxui'
import { Icon } from '@mxenabled/mx-icons'

import { startOfMonth } from 'date-fns/startOfMonth'
import { endOfMonth } from 'date-fns/endOfMonth'

import {
  CurrencyDialog,
  useAccountStore,
  useBudgetsStore,
  useGlobalCopyStore,
  useTransactionStore,
} from '../../../../../common'
import { type Transaction } from '../../../../../core'
import { useAriaLive } from '../../../../../common/hooks/useAriaLive'
import MerchantBudgetDetailsDrawer from '../../../../../budgets/components/MerchantBudgetDetailsDrawer'
import { formatCurrency } from '../../../../../core/utils/NumberFormatting'
import { buildDynamicCopy } from '../../../../../core/utils/Localization'

interface AddMerchantBudgetActionProps {
  transaction: Transaction
}

export enum CurrencyDialogAction {
  CREATE = 'create',
  EDIT = 'edit',
  DELETE = 'delete',
}

export const AddMerchantBudgetAction = observer(({ transaction }: AddMerchantBudgetActionProps) => {
  const [currencyDialogAction, setCurrencyDialogAction] =
    React.useState<CurrencyDialogAction | null>(null)

  const [amount, setAmount] = React.useState('')

  const inProgressRef = React.useRef(false)

  const [lastActionType, setLastActionType] = React.useState<CurrencyDialogAction | null>(null)

  const theme = useTheme()

  const {
    addMerchantBudget,
    updateMerchantBudget,
    deleteMerchantBudget,
    getMerchantBudgetByMerchantGuid,
    alert,
    setAlert,
  } = useBudgetsStore()
  const { setFilter } = useTransactionStore()
  const { accounts } = useAccountStore()
  const bgColor = theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'

  const { budgets: budgetsCopy, common: commonCopy } = useGlobalCopyStore()

  const { announce, ariaLive } = useAriaLive()

  const [merchantBudgetDetailsDrawerOpen, setMerchantBudgetDetailsDrawerOpen] =
    React.useState(false)

  const existingMerchantBudget = transaction?.merchant_guid
    ? getMerchantBudgetByMerchantGuid(transaction.merchant_guid)
    : undefined

  const handleModalClose = () => {
    setLastActionType(currencyDialogAction)
    setCurrencyDialogAction(null)
    setAmount('')
  }

  const handleClick = () => {
    if (!existingMerchantBudget) {
      setCurrencyDialogAction(CurrencyDialogAction.CREATE)
    } else {
      setMerchantBudgetDetailsDrawerOpen(true)
    }
  }

  // details needed for all the three states of the dialog - create, edit, delete
  const actionDetails = {
    [CurrencyDialogAction.CREATE]: {
      content: buildDynamicCopy(
        budgetsCopy.create_budget_content,
        <strong>{transaction.description}</strong>,
        `${formatCurrency(transaction.amount, '0,0.00')}`,
      ) as string,
      title: budgetsCopy.dialog_create_budget_title,
      primaryButtonText: budgetsCopy.dialog_primary_button_text,
      errorAlertMessage: buildDynamicCopy(
        budgetsCopy.alert_merchant_budget_not_created,
        transaction.description,
      ) as string,
      errorRetryAction: () => setCurrencyDialogAction(CurrencyDialogAction.CREATE),
      successAlertMessage: buildDynamicCopy(
        budgetsCopy.alert_budget_created,
        transaction.description,
      ) as string,
      apiTrigger: async () => await addMerchantBudget(transaction.merchant_guid!, Number(amount)),
    },

    [CurrencyDialogAction.EDIT]: {
      content: buildDynamicCopy(
        budgetsCopy.edit_budget_content,
        <strong>{transaction.description}</strong>,
      ) as string,
      title: budgetsCopy.details_edit_title,
      primaryButtonText: budgetsCopy.add_save_button,
      errorAlertMessage: buildDynamicCopy(
        budgetsCopy.error_alert_edit_merchant_budget,
        transaction.description,
      ) as string,
      errorRetryAction: () => setCurrencyDialogAction(CurrencyDialogAction.EDIT),
      successAlertMessage: buildDynamicCopy(
        budgetsCopy.success_alert_edit_merchant_budget,
        transaction.description,
      ) as string,
      apiTrigger: async () =>
        await updateMerchantBudget({
          ...existingMerchantBudget!,
          amount: Number(amount),
        }),
    },

    [CurrencyDialogAction.DELETE]: {
      content: buildDynamicCopy(
        budgetsCopy.delete_description,
        <strong>{transaction.description}</strong>,
      ) as string,
      title: budgetsCopy.delete_title,
      primaryButtonText: budgetsCopy.details_delete_button,
      errorAlertMessage: buildDynamicCopy(
        budgetsCopy.error_alert_delete_merchant_budget,
        transaction.description,
      ) as string,
      errorRetryAction: () => setCurrencyDialogAction(CurrencyDialogAction.DELETE),
      successAlertMessage: buildDynamicCopy(
        budgetsCopy.success_alert_delete_merchant_budget,
        transaction.description,
      ) as string,
      apiTrigger: async () => await deleteMerchantBudget(existingMerchantBudget!.guid),
    },
  }

  const handlePrimaryAction = async () => {
    if (inProgressRef.current) return

    inProgressRef.current = true

    const { apiTrigger, successAlertMessage, errorAlertMessage } =
      actionDetails[currencyDialogAction as CurrencyDialogAction]

    const { isSuccess } = await apiTrigger()
    const alertMessage = isSuccess ? successAlertMessage : errorAlertMessage
    handleModalClose()
    setAlert({ msg: alertMessage, severity: isSuccess ? 'success' : 'error' })
    announce(alertMessage)

    if (isSuccess && currencyDialogAction === CurrencyDialogAction.DELETE) {
      setMerchantBudgetDetailsDrawerOpen(false)
    }
    inProgressRef.current = false
  }

  React.useEffect(() => {
    const dateRange = {
      start: startOfMonth(new Date(transaction.date * 1000)),
      end: endOfMonth(new Date(transaction.date * 1000)),
    }

    if (merchantBudgetDetailsDrawerOpen) {
      setFilter({
        accounts: accounts.map((account) => account.guid),
        custom: merchantBudgetDetailsDrawerOpen
          ? (trn) => trn.merchant_guid === transaction.merchant_guid
          : (trn) => trn.guid === transaction.guid,
        dateRange,
      })
    }
  }, [merchantBudgetDetailsDrawerOpen, transaction, accounts])

  const snackBar = () => (
    <Toast
      actionText={budgetsCopy.autogenerate_budgets_error_primary_button}
      autoHideDuration={3500}
      closeAriaLabel={commonCopy.close_aria}
      handleClose={() => setAlert(null)}
      message={alert?.msg ?? ''}
      onActionClick={actionDetails[lastActionType as CurrencyDialogAction]?.errorRetryAction}
      open={Boolean(alert)}
      severity={alert?.severity ?? 'success'}
      showAction={alert?.severity === 'error'}
      showClose={true}
    />
  )
  return (
    <React.Fragment>
      {ariaLive}

      <ListItem disableGutters={true} disablePadding={true}>
        <ListItemButton onClick={handleClick}>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: bgColor }} variant="rounded">
              <Icon name="bubble_chart" sx={{ color: 'text.primary' }} />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={budgetsCopy.add_merchant_budget_primary_text}
            secondary={
              <Text bold={true} sx={{ color: 'text.primary' }} variant="body1">
                {existingMerchantBudget
                  ? `${formatCurrency(existingMerchantBudget.amount, '0,0.00')} / ${commonCopy.month_text}`
                  : budgetsCopy.add_merchant_budget_secondary_text}
              </Text>
            }
            slotProps={{ primary: { variant: 'caption', fontSize: 11, fontWeight: 'normal' } }}
          />
          <ListItemIcon>
            <Icon name="chevron_right" sx={{ color: 'text.primary' }} />
          </ListItemIcon>
        </ListItemButton>
      </ListItem>

      {!!existingMerchantBudget && (
        <MerchantBudgetDetailsDrawer
          isOpen={merchantBudgetDetailsDrawerOpen}
          merchantBudget={existingMerchantBudget}
          onClose={() => setMerchantBudgetDetailsDrawerOpen(false)}
          renderingToast={() => alert && snackBar()}
          setCurrencyDialogAction={(action) => {
            if (action === CurrencyDialogAction.EDIT) {
              setAmount(existingMerchantBudget?.amount.toString() || '')
            }
            setCurrencyDialogAction(action)
          }}
          transaction={transaction}
        />
      )}
      {currencyDialogAction && (
        <CurrencyDialog
          closeAriaLabel={commonCopy.close_aria}
          content={actionDetails[currencyDialogAction].content}
          handleInputChange={setAmount}
          handleModalClose={handleModalClose}
          handlePrimaryAction={handlePrimaryAction}
          handleSecondaryAction={handleModalClose}
          inputLabel={
            currencyDialogAction !== CurrencyDialogAction.DELETE ? commonCopy.amount_text : ''
          }
          inputValue={amount}
          isOpen={Boolean(currencyDialogAction)}
          primaryText={actionDetails[currencyDialogAction].primaryButtonText}
          secondaryText={commonCopy.cancel_button}
          title={actionDetails[currencyDialogAction].title}
        />
      )}

      {!!alert && snackBar()}
    </React.Fragment>
  )
})
